import {Icon} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useCallback} from 'react';

import type {Thread} from '../../../agentChat/store/thread';
import {TruncateTextTooltip} from '../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {resolveThreadTitle} from '../threadTitle';
import {getProjectChipLabel} from './projectChip';

interface Props {
  thread: Thread;
  currentEntity: string | null;
  onSelect: (rootTurnId: string) => void;
}

/**
 * HistoryItem renders a single chat-history row. Shows a project chip when
 * the row sits in a non-default project.
 */
export const HistoryItem: React.FC<Props> = ({
  thread,
  currentEntity,
  onSelect,
}) => {
  const title = resolveThreadTitle(thread);
  const rootTurnId = thread.rootTurnId ?? thread.id;
  const chipLabel = getProjectChipLabel(thread, currentEntity);

  const handleSelect = useCallback(() => {
    onSelect(rootTurnId);
  }, [onSelect, rootTurnId]);

  return (
    <div
      onClick={handleSelect}
      className={clsx(
        'flex flex-col gap-[2px] rounded px-[2px] py-[4px]',
        'cursor-pointer hover:bg-moon-100 dark:hover:bg-moon-800'
      )}>
      <div
        className={clsx(
          'min-w-0 flex-1 text-base font-[var(--default-font-family)]',
          'border border-transparent px-[8px]'
        )}>
        <TruncateTextTooltip
          fullText={title}
          truncationType={TruncationType.End}
          noLink
        />
      </div>
      {chipLabel != null && (
        <div className="flex items-center gap-[4px] px-[8px] text-xs text-moon-500 dark:text-moon-400">
          <Icon icon="folder-project" size="xs" />
          <span className="min-w-0 truncate">{chipLabel}</span>
        </div>
      )}
    </div>
  );
};
